export interface CoreStorageConfig<T> {
    readonly version: number;
    readonly defaultState: Readonly<T>;
    readonly validate: (value: unknown) => value is T;
}

export interface CoreStorage<T> {
    readonly save: (state: Readonly<T>, onError: undefined | ((error: Error) => void)) => void;
    readonly load: () => T;
    readonly clear: () => Error | undefined;
    readonly clearAll: () => Error | undefined;
}

const kStoragePrefix = "labs-live";

const createStorageKey = (appDirectory: string, version: number): string =>
    `${kStoragePrefix}:${appDirectory}:${version}`;

const toError = (error: unknown): Error =>
    error instanceof Error ? error : new Error(String(error));

export const createCoreStorage = <T>(
    appDirectory: string,
    config: Readonly<CoreStorageConfig<T>>,
): CoreStorage<T> => {
    const storageKey = createStorageKey(appDirectory, config.version);

    const save: CoreStorage<T>["save"] = (state, onError): void => {
        try {
            const isValid = config.validate(state);
            if (!isValid) {
                onError?.(
                    toError(
                        "Warning: validation failed. The validation function has probably not been updated.",
                    ),
                );
            }

            const serialized = JSON.stringify(state);
            localStorage.setItem(storageKey, serialized);
            return undefined;
        } catch (error: unknown) {
            onError?.(toError(error));
        }
    };

    const load = (): T => {
        try {
            const raw = localStorage.getItem(storageKey);
            if (raw === null) {
                return config.defaultState as T;
            }
            const parsed: unknown = JSON.parse(raw);
            if (!config.validate(parsed)) {
                localStorage.removeItem(storageKey);
                return config.defaultState as T;
            }
            return parsed;
        } catch {
            return config.defaultState as T;
        }
    };

    const clear = (): Error | undefined => {
        try {
            localStorage.removeItem(storageKey);
            return undefined;
        } catch (error: unknown) {
            return toError(error);
        }
    };

    const clearAll = (): Error | undefined => {
        try {
            const prefix = `${kStoragePrefix}:${appDirectory}:`;
            const keysToRemove: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key?.startsWith(prefix) === true) {
                    keysToRemove.push(key);
                }
            }
            for (const key of keysToRemove) {
                localStorage.removeItem(key);
            }
            return undefined;
        } catch (error: unknown) {
            return toError(error);
        }
    };

    return { save, load, clear, clearAll };
};

// For Testing
// -----------------------------------------------------------------------------
export interface MockCoreStorage<T> extends CoreStorage<T> {
    readonly stored: T;
}

export const createMockCoreStorage = <T>(defaultState: Readonly<T>): MockCoreStorage<T> => {
    let _stored: T = defaultState as T;
    return {
        get stored() {
            return _stored;
        },
        save: (state) => {
            _stored = state as T;
        },
        load: () => _stored,
        clear: () => {
            _stored = defaultState as T;
            return undefined;
        },
        clearAll: () => {
            _stored = defaultState as T;
            return undefined;
        },
    };
};
